import { DURABILITY } from '../config';
import type { ItemId } from './inventory';

// TOOL DURABILITY. The inventory is count-based (no per-instance state), so durability is tracked per TOOL ID as
// the HP of the CURRENTLY-wielded unit. Spending it (per landed swing for gather tools; per second + per strike
// for the torch) drains that unit; at 0 the unit BREAKS -- the caller consumes one from the stack and, if a spare
// remains, HP resets to full for the next unit (so a stack of three axes is three full axes). Only ids present in
// the config DURABILITY map degrade; everything else (ak, hammer, deployables) is durability-free. Client-only HP,
// not persisted across reloads (matches a fresh session); for MP this would move authority-side.
export class DurabilityStore {
  private readonly hp = new Map<string, number>();
  private readonly ceil = new Map<string, number>(); // degraded max: each repair lowers this 10% of the original
  private readonly subs: Array<() => void> = [];

  has(id: ItemId): boolean { return DURABILITY[id] != null; }
  max(id: ItemId): number { return DURABILITY[id] ?? 0; }
  // The current ceiling for this id: the original max, lowered 10% per repair. A fresh unit (reset) restores it.
  ceiling(id: ItemId): number { const c = this.ceil.get(id); return c == null ? this.max(id) : c; }
  current(id: ItemId): number {
    const m = this.max(id); if (!m) return 0;
    const v = this.hp.get(id); return v == null ? m : v;
  }
  frac(id: ItemId): number { const m = this.max(id); return m ? Math.max(0, this.current(id) / m) : 1; }

  // Repair at a bench: refill toward the (degrading) ceiling. Each repair permanently lowers the ceiling by 10%
  // of the ORIGINAL max, so a tool can be repaired ~9 times before it is worn out. Returns the new HP, or null if
  // it cannot be repaired (not a durable id, already full, or worn out). Cost is charged by the caller.
  repair(id: ItemId): number | null {
    if (!this.has(id)) return null;
    const orig = this.max(id);
    const newCeil = Math.round(this.ceiling(id) - orig * 0.1);
    if (newCeil <= 0) return null; // worn out -- no ceiling left to refill into
    if (this.current(id) >= this.ceiling(id) && this.ceiling(id) >= orig) return null; // pristine, nothing to do
    this.ceil.set(id, newCeil);
    this.hp.set(id, newCeil);
    this.emit();
    return newCeil;
  }

  // Spend `amount` durability on `id`. Returns true if it JUST hit 0 this call. HP is NOT auto-reset: it stays at
  // 0 = BROKEN. Tools sit broken (can't be used) until a fresh one is crafted/picked up (which calls reset); a
  // torch is consumed by its caller on break instead. An already-broken id no-ops.
  use(id: ItemId, amount = 1): boolean {
    if (!this.has(id) || amount <= 0) return false;
    const cur = this.current(id);
    if (cur <= 0) return false; // already broken
    const v = cur - amount;
    this.hp.set(id, Math.max(0, v));
    this.emit();
    return v <= 0; // just broke
  }

  broken(id: ItemId): boolean { return this.has(id) && this.current(id) <= 0; }

  reset(id: ItemId): void { if (this.has(id)) { this.hp.set(id, this.max(id)); this.ceil.delete(id); this.emit(); } } // a fresh unit: full HP + ceiling reset

  subscribe(fn: () => void): () => void {
    this.subs.push(fn);
    return () => { const i = this.subs.indexOf(fn); if (i >= 0) this.subs.splice(i, 1); };
  }
  private emit(): void { for (const f of this.subs) f(); }
}
